import { useCallback, useEffect, useState } from 'react';
import {
  reports,
  sendBroadcast,
  type BroadcastRow,
  type Category,
  type CoverageReport,
  type Site,
} from '../lib/api.ts';
import { REASON } from './AttachButton.tsx';
import { uploadAttachment } from '../lib/api.ts';
import { Modal } from './Modal';
import { PAGE, Pager } from './Pager';
import { Coverage } from './Coverage.tsx';

interface Props {
  readonly sites: readonly Site[];
  readonly categories: readonly Category[];
}

/**
 * Send a notice to many sites, then watch coverage arrive.
 *
 * Send and report live on one screen deliberately -- the value of the
 * product is the loop between them, and splitting them into separate
 * pages hides that.
 */
export const Notices = ({ sites, categories }: Props) => {
  const [list, setList] = useState<BroadcastRow[]>([]);
  const [page, setPage] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [stage, setStage] = useState('');
  const [open, setOpen] = useState<CoverageReport | null>(null);
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [targets, setTargets] = useState<string[]>([]);
  const [ack, setAck] = useState(false);
  const [urgent, setUrgent] = useState(false);
  const [pinHours, setPinHours] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    reports.broadcasts().then(setList).catch(() => undefined);
  }, []);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    if (categories[0]) setCategoryId(categories[0].id);
    setTargets(sites.map((s) => s.id));
  }, [categories, sites]);

  const send = async () => {
    const text = body.trim();
    if (!text || targets.length === 0) return;

    setBusy(true);
    setError('');
    try {
      const result = await sendBroadcast({
        categoryId,
        siteIds: targets,
        body: text,
        priority: urgent ? 'urgent' : 'normal',
        requiresAck: ack,
        pinHours,
      });

      // A notice fans out to one message per site, so every file has
      // to go to each of them: a photo of a broken part is no use to
      // the sites that happen to open the copy without it.
      for (const [n, f] of files.entries()) {
        setStage(`Sending ${f.name} (${n + 1} of ${files.length})...`);
        for (const id of result.messageIds) {
          const up = await uploadAttachment(id, f);
          if (!up.ok) {
            setError(`${f.name}: ${REASON[up.reason]}`);
            break;
          }
        }
      }
      setFiles([]);
      setStage('');

      setBody('');
      setAck(false);
      setUrgent(false);
      setPinHours(0);
      refresh();
      // Jump straight to coverage -- watching it fill is the point.
      const report = await reports.coverage(result.broadcastId);
      setOpen(report);
    } catch {
      setError('Could not send. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const toggleSite = (id: string) =>
    setTargets((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );


  return (
    <div className="noticeWrap">
      <section className="composer">
        {error && <p className="err">{error}</p>}
        <p className="mono">New notice</p>

        <textarea
          rows={3}
          value={body}
          placeholder="What do the sites need to know?"
          onChange={(e) => setBody(e.target.value)}
        />

        {/* Attached before sending, uploaded after: the API needs a
            message to attach to, and a notice does not exist until
            it has been sent. */}
        <label className="filePick">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            multiple
            onChange={(e) => setFiles(
              e.target.files ? Array.from(e.target.files) : [])}
          />
          <span>
            {files.length === 0
              ? 'Attach photos or PDFs'
              : files.map((f) => f.name).join(', ')}
          </span>
        </label>
        {stage && <p className="sub">{stage}</p>}
        <div className="noticeRow">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            aria-label="Category"
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <p className="mono">
          Sites &mdash; {targets.length} of {sites.length}
        </p>
        <div className="siteChips">
          {sites.map((s) => (
            <button
              key={s.id}
              className={targets.includes(s.id) ? 'chip on' : 'chip'}
              onClick={() => toggleSite(s.id)}
            >
              {s.name}
            </button>
          ))}
        </div>

        <div className="noticeRow">
          <label className="check">
            <input type="checkbox" checked={ack}
              onChange={(e) => setAck(e.target.checked)} />
            Require acknowledgement
          </label>
          <label className="check">
            <input type="checkbox" checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)} />
            Urgent
          </label>
          <label className="check">
            Keep pinned
            <select
              value={pinHours}
              onChange={(e) => setPinHours(Number(e.target.value))}
            >
              <option value={0}>not pinned</option>
              <option value={8}>8 hours</option>
              <option value={24}>1 day</option>
              <option value={72}>3 days</option>
              <option value={168}>1 week</option>
            </select>
          </label>
        </div>

        <button
          className="primary"
          disabled={busy || !body.trim() || targets.length === 0}
          onClick={send}
        >
          {busy ? 'Sending...' : `Send to ${targets.length} sites`}
        </button>
      </section>

      <p className="mono covHead">Sent notices</p>
      {list.length === 0 ? (
        <p className="empty">No notices sent yet.</p>
      ) : (
        <>
        <ul className="covList">
          {list.slice(page * PAGE, page * PAGE + PAGE).map((b) => (
            <li key={b.id}>
              <button
                className="sentRow"
                onClick={() => reports.coverage(b.id).then(setOpen)}
              >
                <span className="sentBody">
                  {b.body.slice(0, 60)}
                  {b.body.length > 60 ? '...' : ''}
                </span>
                <span className="mono sentCount">
                  {b.opened}/{b.site_count} opened
                  <span className="sentGo">Who?</span>
                </span>
              </button>

            </li>
          ))}
        </ul>
        <Pager page={page} count={list.length} onPage={setPage} />
        </>
      )}

      {open && (
        <Modal
          title="Read coverage"
          subtitle={`${open.opened} of ${open.sentTo} sites opened it`}
          onClose={() => setOpen(null)}
          onPrint={() => window.print()}
        >
          <Coverage report={open} />
        </Modal>
      )}
    </div>
  );
};
